// React components
import * as React from "react";
import * as nestedListUtils from './NestedListUtils';
import * as  Immutable from 'immutable';

// internal components
import { isDefined } from '@Utils/Micro';

// Message from REACT:
// > The legacy context API will be removed in a future major version. Use the new context API introduced with version 16.3.
// > The legacy API will continue working for all 16.x releases.
//
// Note:
// new context API is used (https://reactjs.org/docs/context.html that was introduced with version 16.3)
// legacy context API, did not work after v16 update anyway!
// ================================================================================================
interface NestedListContext {
    nestedList: NestedList;
}
// ------------------------------------------------------------------------------------------------
/**
 * Context's (lazy) instance
 */
let _NestedListContext: React.Context<NestedListContext> = null;
/**
 * Context getter
 */
function getNestedListContext(): React.Context<NestedListContext> {
    return _NestedListContext || (_NestedListContext = React.createContext<NestedListContext>({ nestedList: null }));
}
// ------------------------------------------------------------------------------------------------
export interface NestedListProps extends React.Props<NestedList> {
    /**
     * List data.
     */
    data: any;

    /**
     * Callback triggered on list data change.
     */
    onDataChange: (tree: any) => void;

    /**
     * Callback for list data validation.
     */

    validate?: (tree: any) => boolean;

    /**
     * Prop for getting component's children.
     */
    getChildren: (items, draggedId) => JSX.Element;
}
// ------------------------------------------------------------------------------------------------
export interface NestedListState {
    /**
     * List's data
     */
    data: any;

    /**
     * List item currently on drag
     */
    draggedId: any;

    /**
     * Context instance passed to children
     */
    context: NestedListContext
}
// ------------------------------------------------------------------------------------------------
export class NestedList extends React.Component<NestedListProps, NestedListState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props) {
        super(props);
        this.state = {
            data: nestedListUtils.index(nestedListUtils.wrap(props.data)),
            draggedId: undefined,
            context: { nestedList: this }
        };
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps) {
        if (this.props.data !== prevProps.data)
            this.reset();
    }
    // ---------------------------------------------------------------------------------------------------
    reset() {
        this.setState({ data: nestedListUtils.index(nestedListUtils.wrap(this.props.data)), draggedId: undefined });
    }
    // ---------------------------------------------------------------------------------------------------
    preview(newData, draggedId) {
        this.setState({ data: newData, draggedId });
    }
    // ---------------------------------------------------------------------------------------------------
    persist(newData) {
        const data = nestedListUtils.unwrap(nestedListUtils.unindex(newData || this.state.data));
        this.reset();

        if (!nestedListUtils.equals(data, this.props.data)) {
            this.props.onDataChange(data);
        }
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const { Provider } = getNestedListContext();
        return <Provider value={this.state.context}>
            {this.props.getChildren(nestedListUtils.unwrap(this.state.data), this.state.draggedId)}
        </Provider>
    }
}
// ================================================================================================
export interface NestedListItemProps extends React.Props<NestedListItem> {
    /**
     * Item's data
     */
    item: any;
    /**
     * Item's children
     */
    children: JSX.Element;
}
// ------------------------------------------------------------------------------------------------
export interface NestedListItemState { }
// ------------------------------------------------------------------------------------------------
/**
 * Common variable for drag events
 */
let _dragData: {
    sourceList: NestedList,
    sourceItem: any,
    startMouseX: number,
    startLevel: number,
} = null;
// ------------------------------------------------------------------------------------------------
export class NestedListItem extends React.Component<NestedListItemProps, NestedListItemState> {
    // ------------------------------------------------------------------------------------------------
    constructor(props) {
        super(props);
        // bind function(s)
        this._onDragStart = this._onDragStart.bind(this);
        this._onDragOver = this._onDragOver.bind(this);
        this._onDragEnd = this._onDragEnd.bind(this);
        this._onDrop = this._onDrop.bind(this);
    }
    // ------------------------------------------------------------------------------------------------
    private _extractReorderParameters(event: React.MouseEvent<any>, context: NestedListContext) {
        const { pageX } = event;
        const { sourceList, sourceItem } = _dragData;
        const [targetList, targetItem] = [context.nestedList, this.props.item];

        if (sourceItem.get('_id') !== targetItem.get('_id')) {
            _dragData.startLevel = Math.min(this.props.item.get('__level'), sourceItem.get('__level'));
            _dragData.startMouseX = pageX;
        }
        const mouseDeltaX = pageX - _dragData.startMouseX;

        return {
            sourceList,
            targetList,
            sourceItem,
            targetItem,
            level: _dragData.startLevel + Math.floor(mouseDeltaX / 20 + 0.5) - 1
        };
    }
    // ------------------------------------------------------------------------------------------------
    private _onReorder(sourceList: NestedList, targetList: NestedList, sourceItem, targetItem, level: number, preview: boolean = false) {
        if (sourceList === targetList) {
            const newData = nestedListUtils.reorder(sourceList.state.data, sourceItem, targetItem, level, sourceList.props.validate);

            if (preview) {
                if (!Immutable.is(newData, sourceList.state.data)) {
                    sourceList.preview(newData, sourceItem.get('_id'));
                }
            } else {
                sourceList.persist(newData);
            }
        }
    }
    // ------------------------------------------------------------------------------------------------
    private _onReorderReset(sourceList, targetList) {
        sourceList.reset();
        targetList.reset();
    }
    // ------------------------------------------------------------------------------------------------
    private _onDragStart(event, context: NestedListContext) {
        _dragData = {
            sourceList: context.nestedList,
            sourceItem: this.props.item,
            startMouseX: event.pageX,
            startLevel: this.props.item.get('__level')
        };

        if (event.dataTransfer) {
            event.dataTransfer.setData('Url', '#');
            event.dataTransfer.setDragImage(document.createElement('div'), 0, 0);
        }

        context.nestedList.preview(context.nestedList.state.data, this.props.item.get('_id'));
    }
    // ------------------------------------------------------------------------------------------------
    private _onDragOver(event: React.MouseEvent<any>, context: NestedListContext) {
        if (!isDefined(_dragData)) return;

        event.preventDefault();
        const { sourceList, targetList, sourceItem, targetItem, level } = this._extractReorderParameters(event, context);
        this._onReorder(sourceList, targetList, sourceItem, targetItem, level, true);
    }
    // ------------------------------------------------------------------------------------------------
    private _onDragEnd(event: React.MouseEvent<any>, context: NestedListContext) {
        if (!isDefined(_dragData)) return;

        const { sourceList, targetList } = this._extractReorderParameters(event, context);
        this._onReorderReset(sourceList, targetList);
        _dragData = null;
    }
    // ------------------------------------------------------------------------------------------------
    private _onDrop(event: React.MouseEvent<any>, context: NestedListContext) {
        if (!isDefined(_dragData)) return;

        const { sourceList, targetList, sourceItem, targetItem, level } = this._extractReorderParameters(event, context);
        this._onReorder(sourceList, targetList, sourceItem, targetItem, level);
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        const { Consumer } = getNestedListContext();
        return <Consumer>
            {
                (value: NestedListContext) =>
                    React.cloneElement(this.props.children, {
                        onDragStart: (ev) => this._onDragStart(ev, value),
                        onDragOver: (ev) => this._onDragOver(ev, value),
                        onDragEnd: (ev) => this._onDragEnd(ev, value),
                        onDrop: (ev) => this._onDrop(ev, value),
                        draggable: true
                    })
            }
        </Consumer>
    }
}
// ================================================================================================